import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { MessageCircle, Phone, Star, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSettings, waLink, telLink } from "@/lib/site";
import { mediaUrl } from "@/lib/api";

const HERO_IMG =
  "https://images.unsplash.com/photo-1519741497674-611481863552?crop=entropy&cs=srgb&fm=jpg&q=85&w=1100";

const TRUST = ["10+ yıl deneyim", "500+ mutlu çift", "7 günde teslim", "Tüm ilçelere ulaşım"];

export const Hero = () => {
  const settings = useSettings();
  const reduce = useReducedMotion();
  const heroSrc = settings.hero_image
    ? mediaUrl(settings.hero_image)
    : (settings.gallery_is_custom && settings.gallery && settings.gallery[0] ? mediaUrl(settings.gallery[0]) : HERO_IMG);

  return (
    <section className="relative overflow-hidden bg-[#0E0F10] pt-28 sm:pt-32 lg:pt-36 pb-16 lg:pb-24">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(900px circle at 18% 12%, rgba(200,162,74,0.16), transparent 55%), radial-gradient(700px circle at 82% 28%, rgba(246,238,221,0.08), transparent 60%)",
        }}
      />
      <div className="container-lux relative grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
        <motion.div
          initial={reduce ? { opacity: 0 } : { opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className="eyebrow">
            <span className="w-6 h-px bg-[#C8A24A]" /> Ankara & Tüm İlçeler
          </span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-600 leading-[1.05] text-[#F7F2E8] mt-4">
            Ankara'da Gelin Damat <span className="text-[#D7B86A] italic">Dış Çekim</span>
            <br className="hidden sm:block" /> Zamansız, Şık, Sinematik
          </h1>
          <p className="text-[#CFC6B8] text-base sm:text-lg mt-5 max-w-xl leading-relaxed">
            Çankaya'dan Beypazarı'na, Gölbaşı'ndan Tuz Gölü'ne kadar Ankara'nın her ilçesinde
            doğal poz yönlendirmesiyle hayalinizdeki düğün karelerini çekiyoruz. Tarihinizi alın,
            konseptinizi birlikte planlayalım.
          </p>

          <div className="flex flex-wrap items-center gap-3 mt-8">
            <a href={waLink(settings)} target="_blank" rel="noreferrer" data-testid="hero-whatsapp-button">
              <Button size="lg" className="bg-[#C8A24A] text-[#141517] hover:bg-[#D7B86A] font-semibold h-12 px-6">
                <MessageCircle className="w-5 h-5 mr-2" /> WhatsApp'tan Yaz
              </Button>
            </a>
            <a href={telLink(settings)} data-testid="hero-call-button">
              <Button size="lg" variant="outline"
                className="h-12 px-6 border-white/20 text-[#F7F2E8] bg-transparent hover:bg-[rgba(200,162,74,0.08)] hover:border-[rgba(200,162,74,0.55)]">
                <Phone className="w-5 h-5 mr-2" /> Hemen Ara
              </Button>
            </a>
            <Link to="/paketler" className="text-sm text-[#CFC6B8] inline-flex items-center gap-1 hover:text-white px-2">
              Paketleri Gör <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-8">
            <div className="flex items-center gap-1.5">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-4 h-4 fill-[#D7B86A] text-[#D7B86A]" />
              ))}
              <span className="text-sm text-[#CFC6B8] ml-1">4.9/5 · Google</span>
            </div>
            {TRUST.map((t) => (
              <span key={t} className="text-sm text-[#9A9387] flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#C8A24A]" /> {t}
              </span>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
          className="relative"
        >
          <div className="relative rounded-[1.6rem] overflow-hidden border border-white/10 shadow-[0_30px_70px_rgba(0,0,0,0.5)]">
            <img src={heroSrc} alt="Ankara gelin damat dış çekim" loading="eager" data-testid="hero-image"
              className="w-full h-[420px] sm:h-[520px] lg:h-[620px] object-cover"
              style={{ filter: "saturate(1.03) contrast(1.03)" }} />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
          </div>
          <div className="absolute -bottom-5 -left-3 sm:left-6 bg-[#FBF7EF] text-[#141517] rounded-2xl px-5 py-3 shadow-xl border border-black/5">
            <div className="font-display text-2xl leading-none">7 gün</div>
            <div className="text-xs text-[#5B5F66] mt-0.5">dijital teslim</div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
